/**
 * Conversation History Context
 *
 * Provides Firestore-backed conversation history with:
 * - Real-time sync via onSnapshot
 * - Auth-based hydration (loads from Firestore on login)
 * - Bidirectional sync with localStorage
 * - Multi-tab/device synchronization
 * - Rolling 25-message limit
 *
 * IMPORTANT: This context observes the global chat state and syncs to Firestore.
 * It does NOT replace the existing localStorage-based chat system.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react';
import { Unsubscribe } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import type { ChatMessage } from '../components/GlobalChatSystem/types';
import { GLOBAL_CHAT_HISTORY_KEY } from '../components/GlobalChatSystem/types';


import {
  getConversationHistory,
  setConversationHistory,
  onConversationHistorySnapshot,
  flushPendingWrites,
} from '../repositories/conversationHistoryRepository';
import {
  stripStaleLowBalanceMessages_v20260513,
  migrateStaleLowBalanceLocalStorage_v20260513,
} from '../components/GlobalChatSystem/staleLowBalanceFilter';
import { isAnyTabStreaming } from '../components/GlobalChatSystem/streamingFlag';

// ─────────────────────────────────────────────────────────────────────────
// Durability audit rank #6 (2026-07-13): prest0_global_chat_history is NOT
// uid-scoped, and the hydrate path UPLOADS local residue to the CURRENT uid
// when their Firestore copy is empty. On a no-logout account switch (sign-in
// over an existing session / provider re-auth) the clear-on-logout effect
// never fires, so account A's transcript was durably written into account
// B's Firestore. An owner stamp (sibling key, so the array wire-format every
// legacy consumer parses stays untouched) makes ownership explicit: any
// mismatch purges the residue instead of hydrating or uploading it.
// ─────────────────────────────────────────────────────────────────────────
const GLOBAL_CHAT_HISTORY_OWNER_KEY = 'prest0_global_chat_history_owner';

function purgeGlobalHistoryIfForeign(uid: string | null | undefined): void {
  try {
    const owner = localStorage.getItem(GLOBAL_CHAT_HISTORY_OWNER_KEY);
    if (owner && uid && owner !== uid) {
      localStorage.removeItem(GLOBAL_CHAT_HISTORY_KEY);
      localStorage.removeItem(GLOBAL_CHAT_HISTORY_OWNER_KEY);
      console.warn('[ConversationHistory] Purged foreign-account local history (owner mismatch)');
    }
  } catch {
    // storage unavailable — nothing to purge
  }
}

function stampGlobalHistoryOwner(uid: string | null | undefined): void {
  if (!uid) return;
  try {
    localStorage.setItem(GLOBAL_CHAT_HISTORY_OWNER_KEY, uid);
  } catch {
    // best-effort
  }
}

// ============================================================================
// Cross-Tab Streaming Coordination
// ============================================================================

/**
 * Check if any tab is currently streaming a message, via the shared lease in
 * streamingFlag.ts. The old local copy parseInt'd the raw value — after the
 * 2026-07-10 refcount change made the format `count:timestamp`, that parsed
 * the COUNT as a timestamp and always returned false, silently disabling this
 * file's defer-sync-while-streaming guards.
 */
function isStreamingActive(): boolean {
  return isAnyTabStreaming();
}

// ============================================================================
// Temporary Message Filter
// ============================================================================

/**
 * Identifies temporary/transient messages that should NOT be persisted to Firestore.
 * These include: thinking indicators, pending widgets, streaming states, empty completion markers.
 */
function isTemporaryMessage(message: ChatMessage): boolean {
  // A message carrying an active server task ID must be preserved durably so
  // it can be queried and resumed across page lifecycles.
  if (message.serverTaskId) {
    return false;
  }

  // Context-only messages (e.g., document analysis) should ALWAYS be persisted
  // They are hidden from UI but must be in Firestore for AI context
  if (message.isContextOnly) {
    return false;
  }

  // Check 1: sourceTag identifies temporary states
  const temporarySourceTags = new Set([
    // Active streaming/processing states
    'pending',
    'attachment_read_pending',
    'stage1_assessment_pending',
    'translation_streaming',
    'format_streaming',
    'research_result_streaming',
    'video_script_attachment_pending',
    'video_script_analysis_pending',
    'video_script_generation_pending',
    // Thinking/internal processing
    'research_thinking',
    'research_thinking_empty',
    // Empty completion/cleanup markers
    'attachment_read_complete',
    'attachment_read_error',
    'stage1_assessment_complete',
    'stage1_assessment_error',
    // Status/informational (non-content)
    'deep_research_status',
    // NOTE: 'deep_research_qualification' removed - it's user-facing content with interactive buttons
    'attachment_analysis_success',
  ]);

  if (message.sourceTag && temporarySourceTags.has(message.sourceTag)) {
    return true;
  }

  // Check 2: Has pending animation HTML (spinning/shimmer widgets)
  if (
    message.html?.includes('pendingShimmer') ||
    message.html?.includes('pendingSpin') ||
    message.html?.includes('stopped-widget-wrapper')
  ) {
    return true;
  }

  // Check 3: Empty message with no content (non-user messages only)
  if (!message.text?.trim() && !message.html?.trim() && !message.isUser) {
    return true;
  }

  return false;
}

// ============================================================================
// Types
// ============================================================================

interface ConversationHistoryContextValue {
  /** Whether initial hydration from Firestore is complete */
  isHydrated: boolean;
  /** Whether sync is currently enabled */
  isSyncEnabled: boolean;
  /** Force a sync to Firestore */
  syncToFirestore: () => Promise<void>;
  /** Force hydration from Firestore */
  hydrateFromFirestore: () => Promise<void>;
  /**
   * Pause/resume the global Firestore history sync. Surfaces that manage
   * their own per-thread storage (enableThreadSwitcher: true) set this to
   * true so the single-doc global history listener does not flatten
   * thread-local state back into React on every snapshot. Resumed
   * automatically when the surface changes back to single-thread mode.
   */
  setSyncPaused: (paused: boolean) => void;
}

// ============================================================================
// Context
// ============================================================================

const ConversationHistoryContext = createContext<ConversationHistoryContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function ConversationHistoryProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();

  const [isHydrated, setIsHydrated] = useState(false);
  const [isSyncEnabled, setIsSyncEnabled] = useState(false);
  const [syncPaused, setSyncPaused] = useState(false);

  // Refs for tracking state
  const unsubRef = useRef<Unsubscribe | null>(null);
  const lastSyncedVersionRef = useRef<number>(0);
  const isHydratingRef = useRef(false);
  const localUpdatePendingRef = useRef(false);

  const uid = user?.id;

  // ============================================================================
  // Hydration: Load from Firestore on auth
  // ============================================================================

  const hydrateFromFirestore = useCallback(async () => {
    // Rank #6: never hydrate or upload another account's residue.
    purgeGlobalHistoryIfForeign(uid);
    if (!uid || isHydratingRef.current) {
      console.log('[ConversationHistory] Hydration skipped - uid:', uid, 'isHydrating:', isHydratingRef.current);
      return;
    }

    console.log('[ConversationHistory] Starting hydration for uid:', uid);
    isHydratingRef.current = true;

    try {
      // Hotfix #2 (2026-05-13) — sweep stale low-balance banner rows out
      // of localStorage on every hydration entry-point. Idempotent.
      // See `staleLowBalanceFilter.ts` header for the trace.
      try {
        const swept = migrateStaleLowBalanceLocalStorage_v20260513();
        if (swept.rowsRemoved > 0) {
          console.log(
            '[ConversationHistory] Stale low-balance migration removed',
            swept.rowsRemoved, 'rows across', swept.keysRewritten, 'keys.'
          );
        }
      } catch {
        // Non-fatal.
      }

      const firestoreMessagesRaw = await getConversationHistory(uid);
      // Defensive: even if Firestore-side rows survived (legacy data that
      // pre-dated the sanitizer fix), strip them at the read boundary so
      // they never reach React state or localStorage.
      const firestoreMessages = stripStaleLowBalanceMessages_v20260513(firestoreMessagesRaw);
      console.log(
        '[ConversationHistory] Firestore returned',
        firestoreMessagesRaw.length,
        'messages (',
        firestoreMessages.length,
        'after stale-low-balance filter)'
      );

      // Log attachment restoration details
      const messagesWithAttachments = firestoreMessages.filter(m => m.attachmentsSerialized?.length);
      console.log('[ConversationHistory] Hydration attachment analysis:', {
        totalMessages: firestoreMessages.length,
        messagesWithAttachments: messagesWithAttachments.length,
        attachmentDetails: messagesWithAttachments.map(m => ({
          sourceTag: m.sourceTag,
          attachmentCount: m.attachmentsSerialized?.length,
          allHaveStorageUrls: m.attachmentsSerialized?.every(a => !!a.storageUrl),
          attachments: m.attachmentsSerialized?.map(a => ({
            name: a.name,
            hasStorageUrl: !!a.storageUrl,
            hasDataUrl: !!a.dataUrl,
            storageUrlPreview: a.storageUrl?.substring(0, 80),
          })),
        })),
      });

      // Validate attachment integrity - warn about messages with broken references
      const brokenAttachmentMessages = firestoreMessages.filter(m =>
        m.attachmentsSerialized?.some(a => !a.storageUrl && !a.dataUrl && !a.storagePath)
      );
      if (brokenAttachmentMessages.length > 0) {
        console.warn('[ConversationHistory] Found', brokenAttachmentMessages.length,
          'messages with broken attachment references (no storageUrl, dataUrl, or storagePath):',
          brokenAttachmentMessages.map(m => ({
            sourceTag: m.sourceTag,
            attachments: m.attachmentsSerialized?.map(a => ({
              name: a.name,
              hasStorageUrl: !!a.storageUrl,
              hasStoragePath: !!a.storagePath,
              hasDataUrl: !!a.dataUrl,
            })),
          }))
        );
      }

      if (firestoreMessages.length > 0) {
        // Get current localStorage messages
        let localMessages: ChatMessage[] = [];
        try {
          const stored = localStorage.getItem(GLOBAL_CHAT_HISTORY_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) {
              localMessages = parsed.map((m: any) => ({
                ...m,
                timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
              }));
            }
          }
        } catch {
          // Ignore parse errors
        }

        // Merge strategy: Firestore wins if it has newer data
        // Compare by most recent message timestamp
        const firestoreLatest =
          firestoreMessages.length > 0
            ? new Date(firestoreMessages[firestoreMessages.length - 1].timestamp).getTime()
            : 0;
        const localLatest =
          localMessages.length > 0
            ? new Date(localMessages[localMessages.length - 1].timestamp).getTime()
            : 0;

        if (firestoreLatest >= localLatest || localMessages.length === 0) {
          // Firestore is newer or equal - use it
          // Build a lookup of localStorage attachment dataUrls to preserve them.
          // Firestore intentionally strips dataUrl (1MB doc limit), but localStorage needs it
          // for instant local downloads. This prevents data loss during hydration.
          const localDataUrlLookup = new Map<string, Map<string, string>>();
          for (const m of localMessages) {
            const serialized = (m as any).attachmentsSerialized;
            if (Array.isArray(serialized) && serialized.length > 0) {
              const ts = m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp;
              const key = `${(m.text || '').substring(0, 100)}|${ts}`;
              const attMap = new Map<string, string>();
              for (const att of serialized) {
                if (att.dataUrl && att.name) {
                  attMap.set(att.name, att.dataUrl);
                }
              }
              if (attMap.size > 0) {
                localDataUrlLookup.set(key, attMap);
              }
            }
          }

          const payload = firestoreMessages.map((m) => {
            const msg: any = {
              ...m,
              timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
            };

            // Preserve dataUrl from localStorage for attachments that Firestore stripped
            if (msg.attachmentsSerialized?.length && localDataUrlLookup.size > 0) {
              const key = `${(msg.text || '').substring(0, 100)}|${msg.timestamp}`;
              const localAttMap = localDataUrlLookup.get(key);
              if (localAttMap) {
                msg.attachmentsSerialized = msg.attachmentsSerialized.map((att: any) => {
                  if (!att.dataUrl && att.name) {
                    const localDataUrl = localAttMap.get(att.name);
                    if (localDataUrl) {
                      return { ...att, dataUrl: localDataUrl };
                    }
                  }
                  return att;
                });
              }
            }

            return msg;
          });
          localStorage.setItem(GLOBAL_CHAT_HISTORY_KEY, JSON.stringify(payload));
          stampGlobalHistoryOwner(uid);

          // Dispatch storage event for cross-tab sync and to notify any listeners
          window.dispatchEvent(
            new StorageEvent('storage', {
              key: GLOBAL_CHAT_HISTORY_KEY,
              newValue: JSON.stringify(payload),
            })
          );
        } else {
          // Local is newer - sync local to Firestore (filter temporary messages)
          const filteredMessages = localMessages.filter((m) => !isTemporaryMessage(m));
          await setConversationHistory(uid, filteredMessages, { immediate: true });
        }
      } else if (uid) {
        // Firestore is empty - sync local to Firestore if we have local messages
        let localMessages: ChatMessage[] = [];
        try {
          const stored = localStorage.getItem(GLOBAL_CHAT_HISTORY_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              localMessages = parsed.map((m: any) => ({
                ...m,
                timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
              }));
              // Filter temporary messages before syncing to Firestore
              const filteredMessages = localMessages.filter((m) => !isTemporaryMessage(m));
              if (filteredMessages.length > 0) {
                await setConversationHistory(uid, filteredMessages, { immediate: true });
              }
            }
          }
        } catch {
          // Ignore parse errors
        }
      }

      console.log('[ConversationHistory] Hydration complete, enabling sync');
      setIsHydrated(true);
      setIsSyncEnabled(true);
    } catch (error) {
      console.error('[ConversationHistory] Hydration failed:', error);
      // Still enable sync even if hydration fails
      console.log('[ConversationHistory] Enabling sync despite hydration error');
      setIsHydrated(true);
      setIsSyncEnabled(true);
    } finally {
      isHydratingRef.current = false;
    }
  }, [uid]);

  // Hydrate on auth change
  useEffect(() => {
    console.log('[ConversationHistory] Auth state changed - isAuthenticated:', isAuthenticated, 'uid:', uid);
    if (isAuthenticated && uid) {
      // Rank #6: direct account switch (uid A -> uid B with no signed-out
      // transition) never hits the else-branch clear below — purge foreign
      // residue BEFORE hydration can upload it into the new account.
      purgeGlobalHistoryIfForeign(uid);
      hydrateFromFirestore();
    } else {
      // User logged out - reset state
      console.log('[ConversationHistory] User logged out, disabling sync');
      setIsHydrated(false);
      setIsSyncEnabled(false);
      lastSyncedVersionRef.current = 0;

      // SECURITY: Clear localStorage to prevent next visitor from seeing previous user's data
      // This is critical for shared/public computers and prevents cross-user data leakage
      try {
        localStorage.removeItem(GLOBAL_CHAT_HISTORY_KEY);
        localStorage.removeItem(GLOBAL_CHAT_HISTORY_OWNER_KEY);
        console.log('[ConversationHistory] Cleared localStorage chat history for security');
      } catch (e) {
        console.warn('[ConversationHistory] Failed to clear localStorage:', e);
      }
    }
  }, [isAuthenticated, uid, hydrateFromFirestore]);

  // ============================================================================
  // Real-time Firestore Listener
  // ============================================================================

  useEffect(() => {
    // Respect syncPaused too (threading-mode surfaces flip this on so
    // their per-thread persistence can own state without the
    // single-bucket snapshot listener fighting it). Without this guard
    // the Firestore listener would still fire `global-chat-force-sync`
    // events that wipe the active thread's in-flight bubbles
    // (Miles 2026-05-12, Slack thread C0AM8U804KZ/1778481193.723179).
    if (!uid || !isSyncEnabled || syncPaused) {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      return;
    }

    // Subscribe to Firestore changes
    unsubRef.current = onConversationHistorySnapshot(
      uid,
      (rawMessages, version) => {
        // Skip if this is our own write (version matches or is lower)
        if (version <= lastSyncedVersionRef.current) return;

        // Skip if we're currently processing a local update
        if (localUpdatePendingRef.current) return;

        lastSyncedVersionRef.current = version;

        // Hotfix #2 (2026-05-13) — drop stale low-balance banner rows at
        // the snapshot boundary so they cannot re-inject into either
        // localStorage or React state on rehydrate.
        const messages = stripStaleLowBalanceMessages_v20260513(rawMessages);

        // Update localStorage - but preserve active temporary messages (thinking spinners, etc.)
        try {
          // Get current localStorage to preserve temporary messages
          const currentStored = localStorage.getItem(GLOBAL_CHAT_HISTORY_KEY);
          let temporaryMessages: any[] = [];
          if (currentStored) {
            try {
              const currentMessages = JSON.parse(currentStored);
              if (Array.isArray(currentMessages)) {
                // Build a fingerprint set for the incoming Firestore payload so
                // we can identify localStorage rows that have NOT yet round-
                // tripped through Firestore. Hotfix #2 (2026-05-13, Malaika
                // live repro): when a user types "Hello" and Firestore's
                // initial snapshot arrives (lagging the throttled persist),
                // the merged payload below must NOT silently omit the just-
                // sent user bubble — otherwise the downstream
                // `global-chat-force-sync` consumer in useGlobalChat would
                // SET_MESSAGES with a payload missing that bubble.
                const fpOf = (m: any): string => {
                  const t = m?.timestamp instanceof Date
                    ? m.timestamp.getTime()
                    : (typeof m?.timestamp === 'string'
                      ? new Date(m.timestamp).getTime()
                      : Number(m?.timestamp) || 0);
                  return `${m?.isUser ? 'u' : 'a'}|${String(m?.text || '').slice(0, 200)}|${t}`;
                };
                const incomingFingerprints = new Set<string>(
                  messages.map((m) => fpOf(m))
                );
                const incomingIds = new Set<string>(
                  messages
                    .map((m: any) => m?.id)
                    .filter((id: any): id is string => typeof id === 'string')
                );
                const RECENT_WINDOW_MS = 5 * 60 * 1000;
                const now = Date.now();

                // Find temporary messages that should be preserved
                temporaryMessages = currentMessages.filter((m: any) => {
                  // 1) Legacy preservation: thinking/pending/streaming rows
                  if (m.sourceTag && (
                    m.sourceTag.includes('pending') ||
                    m.sourceTag.includes('streaming') ||
                    m.sourceTag.includes('thinking')
                  )) return true;
                  if (m.html?.includes('pendingShimmer') || m.html?.includes('pendingSpin')) return true;

                  // 2) Hotfix #2 preservation: any RECENT row whose
                  //    fingerprint isn't represented in the Firestore
                  //    payload — covers the just-sent user bubble that
                  //    hasn't yet been written upstream. Bounded to a
                  //    5-minute window so we don't leak old rows that
                  //    Firestore has intentionally pruned.
                  if (m.id && incomingIds.has(m.id)) return false;
                  if (incomingFingerprints.has(fpOf(m))) return false;
                  const t = typeof m?.timestamp === 'string'
                    ? new Date(m.timestamp).getTime()
                    : Number(m?.timestamp) || 0;
                  if (!Number.isFinite(t) || t < now - RECENT_WINDOW_MS) return false;
                  return true;
                });
              }
            } catch {
              // Ignore parse errors
            }
          }

          // Build a lookup of localStorage attachment dataUrls by message text+timestamp
          // so we can preserve dataUrls that Firestore intentionally strips (too large for 1MB doc limit)
          const localDataUrlLookup = new Map<string, Map<string, string>>();
          if (currentStored) {
            try {
              const currentMessages = JSON.parse(currentStored);
              if (Array.isArray(currentMessages)) {
                for (const m of currentMessages) {
                  if (m.attachmentsSerialized?.length) {
                    const key = `${m.text?.substring(0, 100)}|${m.timestamp}`;
                    const attMap = new Map<string, string>();
                    for (const att of m.attachmentsSerialized) {
                      if (att.dataUrl && att.name) {
                        attMap.set(att.name, att.dataUrl);
                      }
                    }
                    if (attMap.size > 0) {
                      localDataUrlLookup.set(key, attMap);
                    }
                  }
                }
              }
            } catch {
              // Ignore parse errors - lookup will just be empty
            }
          }

          // Convert Firestore messages to localStorage format
          // Preserve localStorage dataUrls for attachments that lack both storageUrl and dataUrl
          const firestorePayload = messages.map((m) => {
            const msg = {
              ...m,
              timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
            };

            // Preserve dataUrl from localStorage for attachments that Firestore stripped
            // Always restore dataUrl when missing, regardless of storageUrl presence.
            // Having both provides instant local download (dataUrl) + cross-device access (storageUrl).
            if (msg.attachmentsSerialized?.length) {
              const key = `${msg.text?.substring(0, 100)}|${msg.timestamp}`;
              const localAttMap = localDataUrlLookup.get(key);
              if (localAttMap) {
                msg.attachmentsSerialized = msg.attachmentsSerialized.map((att: any) => {
                  if (!att.dataUrl && att.name) {
                    const localDataUrl = localAttMap.get(att.name);
                    if (localDataUrl) {
                      return { ...att, dataUrl: localDataUrl };
                    }
                  }
                  return att;
                });
              }
            }

            return msg;
          });

          // Merge: Firestore messages + preserved temporary messages
          const payload = [...firestorePayload, ...temporaryMessages];

          localStorage.setItem(GLOBAL_CHAT_HISTORY_KEY, JSON.stringify(payload));
          stampGlobalHistoryOwner(uid);

          // Dispatch storage event for cross-tab sync
          window.dispatchEvent(
            new StorageEvent('storage', {
              key: GLOBAL_CHAT_HISTORY_KEY,
              newValue: JSON.stringify(payload),
            })
          );

          // Also dispatch force-sync for same-tab updates (belt-and-suspenders).
          // This bypasses all guards in useGlobalChat and ensures React state updates
          // when Firestore delivers content changes (attachment URLs, token data,
          // button selection state) without changing message count.
          //
          // IMPORTANT (fix/global-chat-disappearing-user-bubble): the event payload
          // must be the *merged* view (firestore + preserved temporary rows), NOT
          // firestore-only. Dispatching a firestore-only payload here causes the
          // consumer (useGlobalChat.handleForceSync) to SET_MESSAGES with a list
          // that omits any in-flight user bubble / pending-thinking row that hasn't
          // yet round-tripped through firestore, wiping the user's just-sent
          // message from React state (see `memory` 2026-05-08). Using the merged
          // `payload` keeps in-memory/localStorage-only rows intact.
          window.dispatchEvent(
            new CustomEvent('global-chat-force-sync', {
              detail: {
                messages: payload.map((m: any) => ({
                  ...m,
                  timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
                })),
              },
            })
          );
        } catch (error) {
          console.error('[ConversationHistory] Failed to update localStorage:', error);
        }
      },
      (error) => {
        console.error('[ConversationHistory] Listener error:', error);
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [uid, isSyncEnabled, syncPaused]);

  // ============================================================================
  // Sync localStorage changes to Firestore
  // ============================================================================

  const syncToFirestore = useCallback(async () => {
    if (!uid || !isSyncEnabled || syncPaused) {
      console.log('[ConversationHistory] Sync skipped - uid:', uid, 'isSyncEnabled:', isSyncEnabled, 'syncPaused:', syncPaused);
      return;
    }

    // Skip sync while streaming is active - prevents last_updated from updating during stream
    if (isStreamingActive()) {
      console.log('[ConversationHistory] Sync deferred - streaming active, will retry in 5s');
      // Schedule retry after streaming likely completes (5 seconds = streaming timeout window)
      setTimeout(() => {
        syncToFirestore();
      }, 5000);
      return;
    }

    try {
      console.log('[ConversationHistory] Starting sync for uid:', uid);
      const stored = localStorage.getItem(GLOBAL_CHAT_HISTORY_KEY);
      if (!stored) {
        console.log('[ConversationHistory] No localStorage data found');
        return;
      }

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) {
        console.log('[ConversationHistory] localStorage data is not an array');
        return;
      }

      const allMessages: ChatMessage[] = parsed.map((m: any) => ({
        ...m,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
      }));

      // Filter out temporary messages (thinking spinners, pending widgets, etc.)
      const messages = allMessages.filter((m) => !isTemporaryMessage(m));

      const filteredCount = allMessages.length - messages.length;
      if (filteredCount > 0) {
        console.log('[ConversationHistory] Filtered', filteredCount, 'temporary messages');
      }
      console.log('[ConversationHistory] Syncing', messages.length, 'messages to Firestore');

      localUpdatePendingRef.current = true;
      // Use immediate: true to await the actual write and catch errors properly
      await setConversationHistory(uid, messages, { immediate: true });
      console.log('[ConversationHistory] Sync completed successfully');
      lastSyncedVersionRef.current += 1;
      localUpdatePendingRef.current = false;
    } catch (error) {
      console.error('[ConversationHistory] Sync to Firestore failed:', error);
      localUpdatePendingRef.current = false;
    }
  }, [uid, isSyncEnabled, syncPaused]);

  // Watch localStorage directly for changes (polls every 200ms)
  // This works regardless of whether GlobalChatProvider is present
  useEffect(() => {
    if (!uid || !isSyncEnabled || syncPaused) {
      console.log('[ConversationHistory] Polling not started - uid:', uid, 'isSyncEnabled:', isSyncEnabled, 'syncPaused:', syncPaused);
      return;
    }

    console.log('[ConversationHistory] Starting localStorage polling for uid:', uid);
    let lastStoredValue = localStorage.getItem(GLOBAL_CHAT_HISTORY_KEY) || '[]';
    let syncTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkForChanges = () => {
      const currentValue = localStorage.getItem(GLOBAL_CHAT_HISTORY_KEY) || '[]';

      if (currentValue !== lastStoredValue) {
        lastStoredValue = currentValue;

        // Clear any pending sync
        if (syncTimeoutId) {
          clearTimeout(syncTimeoutId);
        }

        // Skip immediate sync if streaming is active - schedule retry for after streaming completes
        if (isStreamingActive()) {
          // Schedule sync for after streaming window (5 seconds + 100ms buffer)
          if (!syncTimeoutId) {
            syncTimeoutId = setTimeout(() => {
              syncToFirestore();
              syncTimeoutId = null;
            }, 5100);
          }
          return;
        }

        // Short debounce since messages are immutable
        syncTimeoutId = setTimeout(() => {
          syncToFirestore();
          syncTimeoutId = null;
        }, 100);
      }
    };

    // Poll for changes (2000ms = 10x reduction from original 200ms for performance)
    const intervalId = setInterval(checkForChanges, 2000);

    // Also listen for cross-tab storage events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== GLOBAL_CHAT_HISTORY_KEY) return;
      lastStoredValue = e.newValue || '[]';
      // Skip immediate sync if streaming is active - schedule retry for after streaming completes
      if (isStreamingActive()) {
        if (!syncTimeoutId) {
          syncTimeoutId = setTimeout(() => {
            syncToFirestore();
            syncTimeoutId = null;
          }, 5100);
        }
      } else {
        syncToFirestore();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(intervalId);
      if (syncTimeoutId) {
        clearTimeout(syncTimeoutId);
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [uid, isSyncEnabled, syncPaused, syncToFirestore]);

  // ============================================================================
  // Force-sync listener: ensures Firestore receives attachment data immediately
  // after message finalization (bypasses 2s polling interval)
  // ============================================================================

  useEffect(() => {
    if (!uid || !isSyncEnabled || syncPaused) return;

    const handleForceSync = () => {
      console.log('[ConversationHistory] Force sync triggered - ensuring attachments reach Firestore');
      syncToFirestore();
    };

    window.addEventListener('global-chat-force-sync', handleForceSync);
    return () => window.removeEventListener('global-chat-force-sync', handleForceSync);
  }, [uid, isSyncEnabled, syncPaused, syncToFirestore]);

  // ============================================================================
  // Cleanup on unmount/logout
  // ============================================================================

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (uid) {
        flushPendingWrites(uid);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (uid) {
        flushPendingWrites(uid);
      }
    };
  }, [uid]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: ConversationHistoryContextValue = {
    isHydrated,
    isSyncEnabled: isSyncEnabled && !syncPaused,
    syncToFirestore,
    hydrateFromFirestore,
    setSyncPaused,
  };

  return (
    <ConversationHistoryContext.Provider value={value}>
      {children}
    </ConversationHistoryContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

export function useConversationHistory(): ConversationHistoryContextValue {
  const context = useContext(ConversationHistoryContext);
  if (!context) {
    throw new Error('useConversationHistory must be used within ConversationHistoryProvider');
  }
  return context;
}

export function useMaybeConversationHistory(): ConversationHistoryContextValue | null {
  return useContext(ConversationHistoryContext);
}
